import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { darken } from '@material-ui/core/styles/colorManipulator';
import { DS_SIZES } from 'Components/ComponentLibrary/DesignSystem/sizes';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import SelectControl from 'Components/UI/SelectControl';

export const COLORS = ['white'];

// note, this is needed to override styles because Material changes the order of classes
const important = style => {
  return `${style} !important`;
};

const styles = theme => ({
  rootHidden: {
    display: 'none',
  },
  formControl: {
    background: 'transparent',
  },
  menu: {
    maxHeight: '200px',
  },
  paddingDense: {
    padding: theme.spacing(0.5, 1),
  },
  paddingNone: {
    padding: 0,
  },
  selectControl: {
    paddingBottom: 0,
  },
  select: {
    borderRadius: important(`${theme.radius.md}px`),
    fontWeight: important(theme.typography.fontWeightFacetBold),
    textAlign: important('center'),
    '& + .MuiSelect-nativeInput': {
      height: important('0px'),
      width: important('0px'),
    },
  },
  sizeExtraSmall: {
    fontSize: important(theme.typography.body1.fontSize),
    lineHeight: important('1.5'),
    '& ~ .MuiSelect-icon': {
      height: important('1.25rem'),
      right: '3px',
      top: '3px',
      width: important('1.25rem'),
    },
  },
  variantContainedAccentBlue20: {
    backgroundColor: important(theme.palette.facet.accentBlue20),
    color: important(theme.palette.primary.main),
    '& ~ .MuiSelect-icon': {
      color: important(theme.palette.primary.main),
    },
    '&:hover': {
      backgroundColor: important(
        darken(
          theme.palette.facet.accentBlue20,
          theme.palette.action.hoverOpacity,
        ),
      ),
    },
  },
  variantContainedWhite: {
    backgroundColor: important(theme.palette.common.white),
    color: important(theme.palette.primary.main),
    '& ~ .MuiSelect-icon': {
      color: important(theme.palette.primary.main),
    },
    '&:hover': {
      backgroundColor: important(
        darken(theme.palette.common.white, theme.palette.action.hoverOpacity),
      ),
    },
  },
});

const Select = ({
  classes,
  color,
  hidden,
  onChange,
  options,
  padding,
  size,
  test,
  value,
  variant,
}) => {
  const handleChange = React.useCallback(
    (event, item) => {
      onChange({ event, item, value: event.target.value });
    },
    [onChange],
  );

  return (
    <SelectControl
      className={classNames(classes.selectControl, {
        [classes.rootHidden]: hidden,
      })}
      onChange={handleChange}
      options={options}
      selectId={test}
      SelectProps={{
        className: classes.formControl,
        classes: {
          root: classNames(classes.select, {
            [classes.paddingDense]: padding === 'dense',
            [classes.paddingNone]: padding === 'none',
            [classes.sizeExtraSmall]: size === 'xs',
            [classes.variantContainedWhite]:
              variant === 'contained' && color === 'white',
            [classes.variantContainedAccentBlue20]:
              variant === 'contained' && color === 'accentBlue20',
          }),
        },
        disableUnderline: true,
        MenuProps: {
          classes: {
            paper: classes.menu,
          },
        },
      }}
      test={test}
      value={String(value)}
    />
  );
};

Select.defaultProps = {
  color: undefined,
  onChange: () => {},
  padding: 'none',
  size: 'md',
  test: 'something',
  value: '',
  variant: 'contained',
  hidden: false,
};

Select.propTypes = {
  classes: PropTypes.object.isRequired,
  hidden: PropTypes.bool,
  color: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  padding: PropTypes.string,
  size: PropTypes.oneOf(DS_SIZES),
  test: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.string,
};

export default withStyles(styles)(Select);
